import React, { useState } from 'react';
import { Box, Skeleton, Text, Toggle } from '@hopskipdrive/react-components';

import { MultiSelect } from '../../../components/MultiSelect';
import { StatusChip } from '../../../components/shared';
import { SRSMultiSelect } from '../../../components/SRSMultiSelect';
import { useAuth } from '../../../contexts/AuthContext';
import { useUserState } from '../../../contexts/UserStateContext';
import { useUpdateStatus } from '../../../hooks/useUpdateStatus';
import { getQueryString, isSupervisor } from '../Alerts.helpers';
import { QueryConfig, SupervisorParams, UserParams } from '../types';

import { FilterNavContainer } from './FilterNav.styles';

interface UserFilterNavProps {
  params: UserParams;
  setParams: (params: UserParams) => void;
  alertTypeOptions?: { value: string; label: string }[];
}

interface SupervisorFilterNavProps extends UserFilterNavProps {
  params: SupervisorParams;
  setParams: (params: SupervisorParams) => void;
  employees: Employee[];
}

type FilterNavProps = UserFilterNavProps | SupervisorFilterNavProps;

const FilterNav: React.FC<FilterNavProps> = (props) => {
  const { user } = useAuth();
  const { params, setParams, alertTypeOptions } = props;
  const supervisorParams = isSupervisor(user) ? (params as SupervisorParams) : null;
  const employees = isSupervisor(user) ? (props as SupervisorFilterNavProps).employees : [];
  const [showSnoozed, setShowSnoozed] = useState(params.snoozed ? 'Yes' : 'No');
  const [viewUnassignedOnly, setViewUnassignedOnly] = useState(
    supervisorParams?.assignees?.includes('') ? 'Yes' : 'No'
  );

  const { employeesState, isLoading } = useUpdateStatus(user, employees);
  const { userState, handleUpdateUserStatus } = useUserState();

  const handleIncidentTypeFilter = (incidentTypes: string[]) => {
    setParams({ ...params, incident_types: incidentTypes });
    const queryString = createQueryString(props, { incidentTypes: incidentTypes });

    window.history.pushState('', '', `/alerts${queryString ? `?${queryString}` : ''}`);
  };

  const handleCSFilter = (employeeIds: string[]) => {
    setViewUnassignedOnly('No');
    const ids = [...employeeIds];

    setParams({ ...params, assignees: ids });
    const queryString = createQueryString(props, { handlers: ids });

    window.history.pushState('', '', `/alerts${queryString ? `?${queryString}` : ''}`);
  };

  const handleSnoozeFilter = (value) => {
    const snoozed = value === 'Yes';

    setShowSnoozed(value);
    setParams({ ...params, snoozed: snoozed });
    const queryString = createQueryString(props, { snoozed: snoozed });

    window.history.pushState('', '', `/alerts${queryString ? `?${queryString}` : ''}`);
  };

  const handleViewUnassignedOnlyFilter = (value) => {
    const unassignedOnly = value === 'Yes' ? [''] : supervisorParams?.assignees?.filter((a) => a);

    setViewUnassignedOnly(value);
    setParams({ ...params, assignees: unassignedOnly });
    const queryString = createQueryString(props, { handlers: unassignedOnly });

    window.history.pushState('', '', `/alerts${queryString ? `?${queryString}` : ''}`);
  };
  const createQueryString = (_props: FilterNavProps, options?: QueryConfig) => {
    const defaults = {
      incidentTypes: params.incident_types || [],
      handlers: supervisorParams?.assignees || [],
      snoozed: params.snoozed,
    };

    return getQueryString({ ...defaults, ...options }, isSupervisor(user));
  };

  return (
    <FilterNavContainer data-testid="alerts-filter-nav">
      <Box mb="60px">
        <Box mb="5px">
          <StatusChip
            employeeId={user?.id}
            employeeName={user?.displayName}
            status={userState?.srsAvailability.status}
            updateStatus={handleUpdateUserStatus}
            isLoading={isLoading}
          />
        </Box>

        <Text mb={2} variant="h4" textColor="gray">
          ALERTS
        </Text>
        {alertTypeOptions?.length ? (
          <MultiSelect
            options={alertTypeOptions}
            handleChange={handleIncidentTypeFilter}
            types={params.incident_types ? params?.incident_types : []}
          />
        ) : (
          <Skeleton width={180} />
        )}
        <Box mb="5px">
          <Text mb={2} variant="h4" textColor="gray">
            Show Snoozed Alerts
          </Text>
          <Toggle
            value={showSnoozed}
            onChange={(_e, v) => {
              if (v) {
                handleSnoozeFilter(v);
              }
            }}
            exclusive
          >
            <Toggle.Option data-testid="show-snooze-alerts" value="Yes">
              Yes
            </Toggle.Option>
            <Toggle.Option data-testid="hide-snooze-alerts" value="No">
              No
            </Toggle.Option>
          </Toggle>
        </Box>
        {isSupervisor(user) && (
          <Box>
            <Text mb={2} variant="h4" textColor="gray">
              View Unassigned Only
            </Text>
            <Toggle
              value={viewUnassignedOnly}
              onChange={(_e, v) => {
                if (v) {
                  handleViewUnassignedOnlyFilter(v);
                }
              }}
              exclusive
            >
              <Toggle.Option data-testid="show-unassigned-only-alerts" value="Yes">
                Yes
              </Toggle.Option>
              <Toggle.Option data-testid="hide-unassigned-alerts" value="No">
                No
              </Toggle.Option>
            </Toggle>
          </Box>
        )}
      </Box>
      <Box overflow="auto" height={'calc(100% - 250px)'}>
        {isSupervisor(user) && (
          <SRSMultiSelect
            employees={employeesState}
            handleChange={handleCSFilter}
            handlers={supervisorParams?.assignees ? supervisorParams?.assignees : undefined}
            srsSupervisor={true}
            customSrsSupervisorProps={{
              updateStatus: handleUpdateUserStatus,
              isLoading: isLoading,
            }}
          />
        )}
      </Box>
    </FilterNavContainer>
  );
};

export default FilterNav;
